import { supabase } from '../lib/supabase';
import Papa from 'papaparse';

export const bankTransferService = {
  // ============================================================================
  // EMPLOYEE BANK ACCOUNTS
  // ============================================================================

  async getEmployeeBankAccounts(employeeId) {
    try {
      const { data, error } = await supabase
        ?.from('employee_bank_accounts')
        ?.select('*')
        ?.eq('employee_id', employeeId)
        ?.order('is_primary', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        employeeId: row?.employee_id,
        bankName: row?.bank_name,
        bankCode: row?.bank_code,
        branchCode: row?.branch_code,
        accountNumber: row?.account_number,
        accountHolderName: row?.account_holder_name,
        accountType: row?.account_type,
        isPrimary: row?.is_primary,
        isActive: row?.is_active,
        verifiedAt: row?.verified_at,
        verifiedBy: row?.verified_by,
        notes: row?.notes,
        createdAt: row?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching employee bank accounts:', error);
      throw error;
    }
  },

  async createEmployeeBankAccount(accountData) {
    try {
      const { data, error } = await supabase
        ?.from('employee_bank_accounts')
        ?.insert({
          employee_id: accountData?.employeeId,
          bank_name: accountData?.bankName,
          bank_code: accountData?.bankCode,
          branch_code: accountData?.branchCode,
          account_number: accountData?.accountNumber,
          account_holder_name: accountData?.accountHolderName,
          account_type: accountData?.accountType,
          is_primary: accountData?.isPrimary,
          is_active: accountData?.isActive,
          notes: accountData?.notes
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        employeeId: data?.employee_id,
        bankName: data?.bank_name,
        accountNumber: data?.account_number
      };
    } catch (error) {
      console.error('Error creating employee bank account:', error);
      throw error;
    }
  },

  // ============================================================================
  // EFT TRANSFER BATCH CREATION
  // ============================================================================

  async createTransferBatchFromPayrollRun(payrollRunId, bankName, createdBy) {
    try {
      // 1. Get payroll run details
      const { data: payrollRun, error: runError } = await supabase
        ?.from('payroll_runs')
        ?.select('*')
        ?.eq('id', payrollRunId)
        ?.eq('payroll_status', 'processed')
        ?.single();

      if (runError) throw runError;
      if (!payrollRun) throw new Error('Payroll run not found or not processed');

      // 2. Get payslips with employee bank accounts
      const { data: payslips, error: payslipsError } = await supabase
        ?.from('payslips')
        ?.select(`
          *,
          employees (
            id,
            employee_number,
            first_name,
            last_name
          ),
          payroll_details (
            net_pay
          )
        `)
        ?.eq('payroll_run_id', payrollRunId)
        ?.eq('payslip_status', 'generated');

      if (payslipsError) throw payslipsError;
      if (!payslips || payslips?.length === 0) {
        throw new Error('No generated payslips found for this payroll run');
      }

      // 3. Get bank accounts for employees
      const employeeIds = payslips?.map(p => p?.employee_id);
      const { data: bankAccounts, error: accountsError } = await supabase
        ?.from('employee_bank_accounts')
        ?.select('*')
        ?.in('employee_id', employeeIds)
        ?.eq('is_active', true)
        ?.eq('is_primary', true);

      if (accountsError) throw accountsError;

      // 4. Create bank account lookup
      const accountLookup = {};
      bankAccounts?.forEach(account => {
        accountLookup[account?.employee_id] = account;
      });

      // 5. Validate and prepare transfer records
      const validRecords = [];
      const invalidRecords = [];
      let totalAmount = 0;

      payslips?.forEach(payslip => {
        const account = accountLookup?.[payslip?.employee_id];
        const netPay = payslip?.payroll_details?.net_pay || 0;

        if (!account) {
          invalidRecords?.push({
            payslipId: payslip?.id,
            employeeId: payslip?.employee_id,
            employeeName: `${payslip?.employees?.first_name} ${payslip?.employees?.last_name}`,
            error: 'No active bank account found'
          });
          return;
        }

        if (netPay <= 0) {
          invalidRecords?.push({
            payslipId: payslip?.id,
            employeeId: payslip?.employee_id,
            employeeName: `${payslip?.employees?.first_name} ${payslip?.employees?.last_name}`,
            error: 'Invalid net pay amount'
          });
          return;
        }

        // Filter by bank if specified
        if (bankName && account?.bank_name !== bankName) {
          return;
        }

        validRecords?.push({
          payslipId: payslip?.id,
          employeeId: payslip?.employee_id,
          bankAccountId: account?.id,
          employeeNumber: payslip?.employees?.employee_number,
          employeeName: `${payslip?.employees?.first_name} ${payslip?.employees?.last_name}`,
          bankName: account?.bank_name,
          bankCode: account?.bank_code,
          branchCode: account?.branch_code,
          accountNumber: account?.account_number,
          accountHolderName: account?.account_holder_name,
          transferAmount: netPay,
          paymentReference: payslip?.payslip_number
        });

        totalAmount += netPay;
      });

      if (validRecords?.length === 0) {
        throw new Error('No valid transfer records found. Please ensure employees have active bank accounts.');
      }

      // 6. Generate batch number
      const datePrefix = new Date()?.toISOString()?.split('T')?.[0]?.replace(/-/g, '');
      const batchNumber = `EFT-${datePrefix}-${String(Math?.floor(Math?.random() * 1000))?.padStart(3, '0')}`;

      // 7. Create transfer batch
      const { data: batch, error: batchError } = await supabase
        ?.from('eft_transfer_batches')
        ?.insert({
          batch_number: batchNumber,
          payroll_run_id: payrollRunId,
          batch_status: 'draft',
          bank_name: bankName || validRecords?.[0]?.bankName,
          total_records: validRecords?.length,
          total_amount_kina: totalAmount,
          validated_records: validRecords?.length,
          failed_records: invalidRecords?.length,
          file_format: 'ach',
          validation_errors: invalidRecords?.length > 0 ? { invalidRecords } : null,
          created_by: createdBy
        })
        ?.select()
        ?.single();

      if (batchError) throw batchError;

      // 8. Create transfer records
      const recordsToInsert = validRecords?.map(record => ({
        batch_id: batch?.id,
        payslip_id: record?.payslipId,
        employee_id: record?.employeeId,
        bank_account_id: record?.bankAccountId,
        record_status: 'validated',
        employee_number: record?.employeeNumber,
        employee_name: record?.employeeName,
        bank_name: record?.bankName,
        bank_code: record?.bankCode,
        branch_code: record?.branchCode,
        account_number: record?.accountNumber,
        account_holder_name: record?.accountHolderName,
        transfer_amount_kina: record?.transferAmount,
        payment_reference: record?.paymentReference
      }));

      const { error: recordsError } = await supabase
        ?.from('eft_transfer_records')
        ?.insert(recordsToInsert);

      if (recordsError) throw recordsError;

      return {
        success: true,
        batchId: batch?.id,
        batchNumber: batch?.batch_number,
        totalRecords: validRecords?.length,
        totalAmount: totalAmount,
        invalidRecords: invalidRecords
      };
    } catch (error) {
      console.error('Error creating transfer batch:', error);
      throw error;
    }
  },

  // ============================================================================
  // GET TRANSFER BATCHES
  // ============================================================================

  async getTransferBatches() {
    try {
      const { data, error } = await supabase
        ?.from('eft_transfer_batches')
        ?.select(`
          *,
          payroll_runs (
            pay_period_start,
            pay_period_end,
            payment_date
          )
        `)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        batchNumber: row?.batch_number,
        payrollRunId: row?.payroll_run_id,
        batchStatus: row?.batch_status,
        bankName: row?.bank_name,
        totalRecords: row?.total_records,
        totalAmountKina: row?.total_amount_kina,
        validatedRecords: row?.validated_records,
        failedRecords: row?.failed_records,
        fileFormat: row?.file_format,
        fileName: row?.file_name,
        fileUrl: row?.file_url,
        fileGeneratedAt: row?.file_generated_at,
        validationErrors: row?.validation_errors,
        createdBy: row?.created_by,
        createdAt: row?.created_at,
        sentAt: row?.sent_at,
        payPeriod: row?.payroll_runs ? {
          start: row?.payroll_runs?.pay_period_start,
          end: row?.payroll_runs?.pay_period_end,
          paymentDate: row?.payroll_runs?.payment_date
        } : null
      })) || [];
    } catch (error) {
      console.error('Error fetching transfer batches:', error);
      throw error;
    }
  },

  async getTransferBatchDetails(batchId) {
    try {
      const { data, error } = await supabase
        ?.from('eft_transfer_records')
        ?.select('*')
        ?.eq('batch_id', batchId)
        ?.order('employee_name', { ascending: true });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        batchId: row?.batch_id,
        payslipId: row?.payslip_id,
        employeeId: row?.employee_id,
        recordStatus: row?.record_status,
        employeeNumber: row?.employee_number,
        employeeName: row?.employee_name,
        bankName: row?.bank_name,
        bankCode: row?.bank_code,
        branchCode: row?.branch_code,
        accountNumber: row?.account_number,
        accountHolderName: row?.account_holder_name,
        transferAmountKina: row?.transfer_amount_kina,
        paymentReference: row?.payment_reference,
        validationErrors: row?.validation_errors
      })) || [];
    } catch (error) {
      console.error('Error fetching transfer batch details:', error);
      throw error;
    }
  },

  // ============================================================================
  // GENERATE EFT/ACH FILE
  // ============================================================================

  async generateEFTFile(batchId, fileFormat = 'ach') {
    try {
      // 1. Get batch details
      const { data: batch, error: batchError } = await supabase
        ?.from('eft_transfer_batches')
        ?.select('*')
        ?.eq('id', batchId)
        ?.single();

      if (batchError) throw batchError;

      // 2. Get transfer records
      const records = await this?.getTransferBatchDetails(batchId);

      if (!records || records?.length === 0) {
        throw new Error('No transfer records found for this batch');
      }

      // 3. Generate file based on format
      let fileContent;
      let fileName;

      if (fileFormat === 'ach') {
        fileContent = this?.generateACHFormat(batch, records);
        fileName = `${batch?.batch_number}_ACH.txt`;
      } else if (fileFormat === 'csv') {
        fileContent = this?.generateCSVFormat(batch, records);
        fileName = `${batch?.batch_number}_CSV.csv`;
      } else {
        throw new Error('Unsupported file format');
      }

      // 4. Update batch status
      await supabase
        ?.from('eft_transfer_batches')
        ?.update({
          batch_status: 'generated',
          file_name: fileName,
          file_generated_at: new Date()?.toISOString()
        })
        ?.eq('id', batchId);

      return {
        success: true,
        fileName: fileName,
        fileContent: fileContent,
        totalRecords: records?.length,
        totalAmount: batch?.total_amount_kina
      };
    } catch (error) {
      console.error('Error generating EFT file:', error);
      throw error;
    }
  },

  // ============================================================================
  // FILE FORMAT GENERATORS
  // ============================================================================

  generateACHFormat(batch, records) {
    // ACH file format for PNG banks (BSP, Kinabank, Credit Bank)
    let fileContent = '';

    // Header record
    fileContent += '1';
    fileContent += batch?.batch_number?.padEnd(20, ' ');
    fileContent += new Date()?.toISOString()?.split('T')?.[0]?.replace(/-/g, '');
    fileContent += String(records?.length)?.padStart(6, '0');
    fileContent += String(batch?.total_amount_kina?.toFixed(2))?.replace('.', '')?.padStart(12, '0');
    fileContent += '\n';

    // Detail records
    records?.forEach((record, index) => {
      fileContent += '2';
      fileContent += String(index + 1)?.padStart(6, '0');
      fileContent += record?.bankCode?.padEnd(4, ' ');
      fileContent += (record?.branchCode || '')?.padEnd(4, ' ');
      fileContent += record?.accountNumber?.padEnd(20, ' ');
      fileContent += record?.accountHolderName?.padEnd(40, ' ');
      fileContent += String(record?.transferAmountKina?.toFixed(2))?.replace('.', '')?.padStart(12, '0');
      fileContent += (record?.paymentReference || '')?.padEnd(20, ' ');
      fileContent += '\n';
    });

    // Trailer record
    fileContent += '9';
    fileContent += String(records?.length)?.padStart(6, '0');
    fileContent += String(batch?.total_amount_kina?.toFixed(2))?.replace('.', '')?.padStart(12, '0');
    fileContent += '\n';

    return fileContent;
  },

  generateCSVFormat(batch, records) {
    // CSV format for manual bank portal submission
    const csvData = records?.map(record => ({
      'Employee Number': record?.employeeNumber,
      'Employee Name': record?.employeeName,
      'Bank Name': record?.bankName?.toUpperCase(),
      'Bank Code': record?.bankCode,
      'Branch Code': record?.branchCode || '',
      'Account Number': record?.accountNumber,
      'Account Holder Name': record?.accountHolderName,
      'Transfer Amount (PGK)': record?.transferAmountKina?.toFixed(2),
      'Payment Reference': record?.paymentReference
    }));

    return Papa?.unparse(csvData);
  },

  // ============================================================================
  // VALIDATION
  // ============================================================================

  async validateBankAccount(accountData) {
    const errors = [];

    // Validate bank code
    if (!accountData?.bankCode || accountData?.bankCode?.length < 3) {
      errors?.push('Invalid bank code');
    }

    // Validate account number
    if (!accountData?.accountNumber || accountData?.accountNumber?.length < 8) {
      errors?.push('Invalid account number');
    }

    // Validate account holder name
    if (!accountData?.accountHolderName || accountData?.accountHolderName?.length < 2) {
      errors?.push('Invalid account holder name');
    }

    return {
      isValid: errors?.length === 0,
      errors: errors
    };
  },

  // ============================================================================
  // STATISTICS
  // ============================================================================

  async getTransferStatistics() {
    try {
      const { data, error } = await supabase
        ?.from('eft_transfer_batches')
        ?.select('batch_status, total_records, total_amount_kina');

      if (error) throw error;

      const stats = {
        totalBatches: data?.length || 0,
        draftBatches: data?.filter(b => b?.batch_status === 'draft')?.length || 0,
        generatedBatches: data?.filter(b => b?.batch_status === 'generated')?.length || 0,
        sentBatches: data?.filter(b => b?.batch_status === 'sent')?.length || 0,
        totalRecords: data?.reduce((sum, b) => sum + (b?.total_records || 0), 0),
        totalAmount: data?.reduce((sum, b) => sum + (b?.total_amount_kina || 0), 0)
      };

      return stats;
    } catch (error) {
      console.error('Error fetching transfer statistics:', error);
      throw error;
    }
  }
};

export default bankTransferService;